import { Merge } from 'react-hook-form';
import { WorkExperience } from '../../../gql-operations/gql-generated';

interface WorkExperienceBIEvent {
    type: string;
    sub_group?: string;
    experience_ids?: string;
    page?: {
        component?: Record<string, any>;
        element?: Record<string, any>;
        impression_position?: number;
    };
}

interface EventParams {
    sellerId?: string;
    workExperienceAmount?: number;
    workExperienceTotal?: number;
    experiencePosition?: number;
    companyId?: string;
    companyName?: string;
    startDate?: WorkExperience['startDate'];
    endDate?: WorkExperience['endDate'];
    workExperiences?: WorkExperience[] | null;
}

const sub_group = 'fiverr_pro';

const generateExperienceIds = (experiences?: WorkExperience[] | null, userId?: string) =>
    JSON.stringify(
        experiences?.map(({ company, startDate, endDate }) =>
            [userId, company.id, startDate, endDate ?? 'null'].join('_')
        )
    );

const workExperienceBIEvents = {
    impressionOnAllExperiences: ({
        sellerId,
        workExperienceAmount,
        workExperienceTotal,
        workExperiences,
    }: Pick<
        EventParams,
        'sellerId' | 'workExperienceAmount' | 'workExperienceTotal' | 'workExperiences'
    >): WorkExperienceBIEvent => ({
        type: 'talent_work_experience_all_is_shown',
        sub_group,
        experience_ids: generateExperienceIds(workExperiences, sellerId),
        page: {
            component: {
                metadata: JSON.stringify({
                    Num_of_total_experience_imps_the_buyer_see: workExperienceAmount,
                    seller_id: sellerId,
                }),
                num_of_elements: workExperienceTotal,
            },
        },
    }),
    impressionOnSingleExperience: ({
        sellerId,
        companyId,
        companyName,
        startDate,
        endDate,
        experiencePosition,
        workExperiences,
    }: Pick<
        EventParams,
        'sellerId' | 'companyId' | 'companyName' | 'startDate' | 'endDate' | 'experiencePosition' | 'workExperiences'
    >): WorkExperienceBIEvent => ({
        type: 'talent_work_experience_item_is_shown',
        sub_group,
        experience_ids: generateExperienceIds(workExperiences, sellerId),
        page: {
            component: {
                metadata: JSON.stringify({
                    company_id: companyId,
                    company_name: companyName,
                    start_date: startDate,
                    end_date: endDate,
                    seller_id: sellerId,
                }),
            },
            impression_position: experiencePosition,
        },
    }),
    clickOnSeeAllExperiences: () => ({
        type: 'clicked_on_see_work_experience',
        sub_group,
    }),
    clickedOnAddWorkExperience: ({ state }: { state?: 'other' | 'failed_state' } = { state: 'other' }) => ({
        type: 'clicked_on_add_work_experience',
        clickType: 'clicked_on_add_work_experience',
        page: {
            element: {
                state,
            },
        },
        sub_group,
    }),
    clickedOnEditWorkExperience: ({
        state,
        sellerId,
        companyId,
        companyName,
        startDate,
        endDate,
        position,
    }: Merge<
        Pick<EventParams, 'sellerId' | 'companyId' | 'companyName' | 'startDate' | 'endDate'>,
        {
            state: 'save' | 'delete';
            position: number;
        }
    >): WorkExperienceBIEvent => ({
        type: 'clicked_on_edit_work_experience',
        page: {
            element: {
                state,
            },
            impression_position: position,
            component: {
                metadata: JSON.stringify({
                    sellerId,
                    companyId,
                    companyName,
                    startDate,
                    endDate,
                }),
            },
        },
        sub_group,
    }),
    clickedOnPublishWorkExperience: () => ({
        type: 'clicked_on_publish_save_work_experience',
        sub_group,
    }),
    clickOnImportWorkExperience: ({ state }: { state?: 'other' | 'failed_state' } = { state: 'other' }) => ({
        type: 'clicked_on_import_work_experience',
        sub_group,
        page: {
            element: {
                state,
            },
        },
    }),
};

export default workExperienceBIEvents;
